/**
 * Typed access to the shared voice-call bridge (SD-211).
 *
 * The bridge itself is a framework-free script whose single source lives in
 * backend/app/templates/voice-call.js — the widget inlines it via Jinja
 * include, we bundle it via this side-effect import. Keep the logic there;
 * this module only adds types.
 */
// @ts-ignore -- plain script (sets window.SuzanohVoiceCall), shared verbatim with the widget
import '../../../backend/app/templates/voice-call.js';

export interface VoiceCallCallbacks {
  onConnecting?: () => void;
  onSessionStart?: (nodeId: string | null) => void;
  /** Agent transcript fragment. reset=true means a new turn: clear the fixed bubble first (§6.2). */
  onAgentText?: (text: string, reset: boolean) => void;
  onUserTranscript?: (text: string) => void;
  onCandidates?: (turnId: number, candidates: string[]) => void;
  onInterrupted?: () => void;
  /** Received-audio level 0..1 for the waveform. */
  onLevel?: (level: number) => void;
  onEndingSoon?: (secondsLeft: number) => void;
  onEnded?: (reason: string) => void;
  onError?: (message: string) => void;
}

export interface VoiceCallController {
  /** Create the AudioContexts; must run synchronously inside the tap handler. */
  prime(): void;
  start(cfg: { token: string; wsUrl?: string; apiBaseUrl?: string; ring?: boolean }): Promise<void>;
  end(): void;
  setMuted(on: boolean): void;
  sendText(text: string): void;
  isMuted(): boolean;
  isActive(): boolean;
  dispose(): void;
}

interface VoiceCallGlobal {
  create(options: { callbacks: VoiceCallCallbacks }): VoiceCallController;
  resolveWsUrl(wsUrl: string | undefined, apiBaseUrl: string): string;
}

declare global {
  interface Window {
    SuzanohVoiceCall?: VoiceCallGlobal;
  }
}

export function createVoiceCall(callbacks: VoiceCallCallbacks): VoiceCallController {
  const bridge = window.SuzanohVoiceCall;
  if (!bridge) throw new Error('voice bridge not loaded');
  return bridge.create({ callbacks });
}
